import { useBreakpointValue } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { FC, PropsWithChildren, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import {
	Box,
	BoxProps,
	ChevronLeftOutline,
	Flex,
	HStack,
	IconButton,
	InformationCircleOutline,
	Portal,
	rem,
	Text,
	Tooltip,
	XOutline,
} from '@bonusly/design-system';
import { useSpoofing } from '@bonusly/who-am-i';

const SPOOFING_BANNER_HEIGHT = 60;
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 60;

interface PanelProps extends BoxProps {
	closeButtonLabel: string;
	/** When true, clicking outside the panel will close it (desktop only) */
	closeOnClickOutside?: boolean;
	/** Content rendered below the scrollable area, pinned to the bottom */
	footer?: React.ReactNode;
	isOpen: boolean;
	/** Content rendered between the header and scrollable area, fixed in place */
	subheader?: React.ReactNode;
	isSidebarExpanded: boolean;
	onBack?: () => void;
	onClose: () => void;
	scrollRef?: RefObject<HTMLDivElement>;
	title: string;
	titleTooltip?: string;
}

export const Panel: FC<PropsWithChildren<PanelProps>> = ({
	children,
	closeButtonLabel,
	closeOnClickOutside = false,
	footer,
	isOpen,
	isSidebarExpanded,
	onBack,
	onClose,
	scrollRef,
	subheader,
	title,
	titleTooltip,
	...props
}) => {
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const { isSpoofing, isDemoing } = useSpoofing();
	const isSpoofingBannerVisible = isSpoofing && !isDemoing;
	const sidebarWidth = isSidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;
	const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;
	const [isScrolled, setIsScrolled] = useState(false);

	// Internal scroll container ref for tracking scroll position
	const internalScrollRef = useRef<HTMLDivElement | null>(null);

	// Track scroll position to show/hide scroll fade
	const handleScroll = useCallback(() => {
		const element = scrollRef?.current ?? internalScrollRef.current;
		if (element) {
			setIsScrolled(element.scrollTop > 0);
		}
	}, [scrollRef]);

	// Callback ref that attaches/detaches the scroll listener when the DOM element mounts/unmounts
	const scrollContainerRef = useCallback(
		(node: HTMLDivElement | null) => {
			const prev = internalScrollRef.current;
			if (prev) {
				prev.removeEventListener('scroll', handleScroll);
			}

			internalScrollRef.current = node;

			if (scrollRef) {
				Object.assign(scrollRef, { current: node });
			}

			if (node) {
				node.addEventListener('scroll', handleScroll, { passive: true });
				setIsScrolled(node.scrollTop > 0);
			} else {
				setIsScrolled(false);
			}
		},
		[handleScroll, scrollRef]
	);

	// Focus the close button when panel opens
	useEffect(() => {
		if (isOpen) {
			requestAnimationFrame(() => {
				closeButtonRef.current?.focus();
			});
		}
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	return (
		<Portal>
			<Global
				styles={{
					'.panel-scroll-fade': {
						WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 28px, black 100%) !important',
						maskImage: 'linear-gradient(to bottom, transparent 0px, black 28px, black 100%) !important',
					},
				}}
			/>
			{/* Invisible backdrop to detect clicks outside - only when enabled and on desktop */}
			{/* Uses same z-index as modal but renders before panel in DOM, so panel appears above it */}
			{closeOnClickOutside && !isMobile && (
				<Box
					position="fixed"
					top={0}
					left={`${sidebarWidth}px`}
					right={0}
					bottom={0}
					zIndex="modal"
					transition="left 0.2s cubic-bezier(.4,0,.2,1)"
					onClick={onClose}
				/>
			)}
			<Flex
				position="fixed"
				top={isSpoofingBannerVisible ? `${SPOOFING_BANNER_HEIGHT + 8}px` : rem(8)}
				left={`${sidebarWidth + 8}px`}
				bottom={rem(8)}
				transition="left 0.2s cubic-bezier(.4,0,.2,1)"
				right={undefined}
				bg="bg.minipage"
				backdropFilter="blur(44px)"
				border={'1px solid'}
				borderColor={'border.default'}
				borderRadius={rem(20)}
				zIndex="modal"
				overflow="visible"
				boxShadow={'base'}
				px={rem(8)}
				h={undefined}
				flexDirection="column"
				{...props}
				w={props.width ?? '100%'}
				// NOTE: this is below props on purpose so we can add mobile styles
				{...(isMobile && {
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					border: 'none',
					borderColor: 'transparent',
					borderRadius: 0,
					boxShadow: 'none',
					h: '100vh',
					w: '100vw',
				})}
			>
				{/* Fixed Header - doesn't scroll */}
				<Box flexShrink={0} pt={rem(20)} pb={rem(12)} px={rem(12)} position="relative">
					<HStack justify="space-between" align="center">
						{onBack ? (
							<Flex
								as="button"
								alignItems="center"
								gap={rem(4)}
								onClick={onBack}
								cursor="pointer"
								_hover={{ opacity: 0.8 }}
								bg="transparent"
								border="none"
								p={0}
							>
								<ChevronLeftOutline boxSize={rem(24)} color="text.default" />
								<Text fontSize={200} fontWeight="semibold">
									{title}
								</Text>
							</Flex>
						) : (
							<Flex alignItems="center" gap={rem(6)}>
								<Text fontSize={200} fontWeight="semibold">
									{title}
								</Text>
								{titleTooltip && (
									<Tooltip label={titleTooltip} placement="bottom">
										<Box as="span" display="inline-flex" color="info.icon">
											<InformationCircleOutline boxSize={rem(20)} />
										</Box>
									</Tooltip>
								)}
							</Flex>
						)}
						<IconButton
							ref={closeButtonRef}
							aria-label={closeButtonLabel}
							icon={<XOutline />}
							size="sm"
							bg="secondary.bgAlpha"
							borderRadius="full"
							onClick={onClose}
							_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'full', outlineOffset: '-2px' }}
							data-testid="panel-close"
						/>
					</HStack>
				</Box>

				{/* Fixed Subheader - doesn't scroll */}
				{subheader && <Box flexShrink={0}>{subheader}</Box>}

				{/* Scrollable Content */}
				<Box
					ref={scrollContainerRef}
					flex={1}
					overflowY="auto"
					overscrollBehavior="contain"
					pb={rem(20)}
					className={isScrolled ? 'panel-scroll-fade' : undefined}
				>
					{children}
				</Box>

				{/* Fixed Footer - doesn't scroll */}
				{footer && <Box flexShrink={0}>{footer}</Box>}
			</Flex>
		</Portal>
	);
};
